import { EventProps } from '@common/components/ItemListingRows/Event';
import { VoucherProps } from '@common/components/ItemListingRows/Voucher';
import { FulfilableBasketItem } from '@features/AddedToBasket/Fulfilable/FulfilableAddedToBasket.types';
import { useLabels } from '@features/Menu/useLabels';
import { useTranslations } from '@lang';
import { some } from '@utils/some';
import { Item } from 'shared-types';
import { separateAndGroupPackageAndNonPackageItems } from '../../ItemListingRows/helpers/separateAndGroupPackageAndNonPackageItems';
import { Stack } from '../../Stack';
import { getModuleLabel, groupByType } from '../BasketDrawer.helpers';
import {
  BasketBannerContainer,
  BasketDrawItemGroupTitle,
  BasketItemListingWrapper,
  BasketListingContainer,
  FlexOne,
} from '../BasketDrawer.styles';
import { AddedToBasketBanner } from '../BasketDrawerBanner/AddedToBasketBanner';
import { DiningListing } from '../DiningListing';
import { FulfilablesListing } from '../FulfilablesListing';
import { GolfListing } from '../GolfListing';
import { PackageListing } from '../PackageListing';
import { ProductListing } from '../ProductListing';
import { Recommendations } from '../Recommendations';
import { RoomListing } from '../RoomListing';
import { SpaListing } from '../SpaListing';

export const BasketDrawerListing = ({
  items,
  modules,
  hideArrivalTimes,
}: {
  items: Item[] | FulfilableBasketItem[];
  modules: string[];
  hideArrivalTimes: boolean;
}) => {
  if (!items || !Array.isArray(items)) {
    return null;
  }

  const groupedItems = separateAndGroupPackageAndNonPackageItems(items, groupByType);
  const { t } = useTranslations();
  const labels = useLabels();

  return (
    <BasketListingContainer>
      <FlexOne>
        <BasketBannerContainer>
          <AddedToBasketBanner />
        </BasketBannerContainer>
        {some(groupedItems.packages) && (
          <BasketItemListingWrapper>
            <PackageListing data-testId="package-listing" packages={groupedItems.packages} />
          </BasketItemListingWrapper>
        )}
        {some(groupedItems.rooms) && (
          <BasketItemListingWrapper>
            <Stack direction="horizontal" justifyContent="flex-start" alignItems="baseline" gap={2}>
              <BasketDrawItemGroupTitle>
                {getModuleLabel('rooms', labels, t)}
              </BasketDrawItemGroupTitle>
              <RoomListing rooms={(groupedItems.rooms ?? []) as unknown as Item[]} />
            </Stack>
          </BasketItemListingWrapper>
        )}
        {some(groupedItems.tables) && (
          <BasketItemListingWrapper>
            <Stack direction="horizontal" justifyContent="flex-start" alignItems="baseline" gap={2}>
              <BasketDrawItemGroupTitle>
                {getModuleLabel('tables', labels, t)}
              </BasketDrawItemGroupTitle>
              <DiningListing tables={(groupedItems.tables || []) as unknown as Item[]} />
            </Stack>
          </BasketItemListingWrapper>
        )}
        {some(groupedItems.spa) && (
          <BasketItemListingWrapper>
            <Stack direction="horizontal" justifyContent="flex-start" alignItems="baseline" gap={2}>
              <BasketDrawItemGroupTitle>
                {getModuleLabel('spa', labels, t)}
              </BasketDrawItemGroupTitle>
              <SpaListing spa={groupedItems.spa ?? []} hideArrivalTimes={hideArrivalTimes} />
            </Stack>
          </BasketItemListingWrapper>
        )}
        {some(groupedItems.golf) && (
          <BasketItemListingWrapper>
            <Stack direction="horizontal" justifyContent="flex-start" alignItems="baseline" gap={2}>
              <BasketDrawItemGroupTitle>
                {getModuleLabel('golf', labels, t)}
              </BasketDrawItemGroupTitle>
              <GolfListing golf={(groupedItems.golf ?? []) as unknown as Item[]} />
            </Stack>
          </BasketItemListingWrapper>
        )}
        {some(groupedItems.products) && (
          <BasketItemListingWrapper>
            <Stack direction="horizontal" justifyContent="flex-start" alignItems="baseline" gap={2}>
              <BasketDrawItemGroupTitle>
                {labels?.retail || t('basketDrawer.products')}
              </BasketDrawItemGroupTitle>
              <ProductListing products={groupedItems.products as unknown as Item[]} />
            </Stack>
          </BasketItemListingWrapper>
        )}
        {some(groupedItems.events) && (
          <BasketItemListingWrapper>
            <Stack direction="horizontal" justifyContent="flex-start" alignItems="baseline" gap={2}>
              <BasketDrawItemGroupTitle>
                {labels?.events || t('basketDrawer.events')}
              </BasketDrawItemGroupTitle>
              <FulfilablesListing fulfilables={groupedItems.events as EventProps[]} />
            </Stack>
          </BasketItemListingWrapper>
        )}
        {some(groupedItems.vouchers) && (
          <BasketItemListingWrapper>
            <Stack direction="horizontal" justifyContent="flex-start" alignItems="baseline" gap={2}>
              <BasketDrawItemGroupTitle>
                {labels?.vouchers || t('basketDrawer.vouchers')}
              </BasketDrawItemGroupTitle>
              <FulfilablesListing fulfilables={groupedItems.vouchers as VoucherProps[]} />
            </Stack>
          </BasketItemListingWrapper>
        )}
      </FlexOne>
      <div>
        <Recommendations modules={modules} />
      </div>
    </BasketListingContainer>
  );
};
